import { useState } from 'react'
import React from 'react'
import { WagmiProvider } from 'wagmi'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { RainbowKitProvider, darkTheme } from '@rainbow-me/rainbowkit'
import { config } from './config/wagmi'
import { Toaster } from 'react-hot-toast'
import { Header } from './components/Header'
import { HeroSection } from './components/HeroSection'
import { MintSection } from './components/MintSection'
import { MyNFTs } from './components/MyNFTs'
import { ContractStats } from './components/ContractStats'
import { DutchAuctionTimer } from './components/DutchAuctionTimer'
import { AdminDebug } from './pages/AdminDebug'
import { LandingPage } from './pages/LandingPage'
import '@rainbow-me/rainbowkit/styles.css'
import './index.css'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 3,
      staleTime: 30000,
      // Use polling instead of WebSocket subscriptions
      refetchInterval: false,
    },
  },
  // Custom logger to suppress noisy WebSocket subscription errors
  logger: {
    log: (...args: unknown[]) => {
      const message = args[0] instanceof Error ? args[0].message : String(args[0] ?? '')
      if (
        message.includes('Connection interrupted') ||
        message.includes('subscribe') ||
        message.includes('relayer') ||
        message.includes('socket')
      ) {
        return
      }
      // eslint-disable-next-line no-console
      console.log(...args)
    },
    warn: (...args: unknown[]) => {
      // eslint-disable-next-line no-console
      console.warn(...args)
    },
    error: (...args: unknown[]) => {
      const message = args[0] instanceof Error ? args[0].message : String(args[0] ?? '')
      const fullMessage = JSON.stringify(args)
      if (
        message.includes('Connection interrupted') ||
        message.includes('subscribe') ||
        message.includes('relayer') ||
        message.includes('socket') ||
        message.includes('Fatal socket error') ||
        fullMessage.includes('relayer') ||
        fullMessage.includes('socket')
      ) {
        return
      }
      // eslint-disable-next-line no-console
      console.error(...args)
    },
  },
} as any)

function DappShell() {
  const [activeTab, setActiveTab] = useState<'mint' | 'admin'>('mint')

  // Suppress non-critical WebSocket subscription errors and block thirdweb requests
  React.useEffect(() => {
    const originalError = window.console.error
    const originalFetch = window.fetch
    
    // Block thirdweb RPC requests
    window.fetch = function (...args: unknown[]) {
      const url = String(args[0] ?? '')
      if (url.includes('thirdweb') || url.includes('11155111.rpc.thirdweb.com')) {
        console.warn('🚫 Blocked thirdweb RPC request:', url)
        return Promise.reject(new Error('thirdweb RPC is blocked. Use alternative RPC.'))
      }
      return originalFetch.apply(this, args as any)
    }
    
    window.console.error = (...args: unknown[]) => {
      const message = String(args[0] ?? '')
      const fullMessage = JSON.stringify(args)
      // Ignore non-critical errors:
      // - WebSocket subscription errors (HTTP still works)
      // - thirdweb errors (we use alternative RPCs)
      // - WalletConnect/Reown demo project errors (not critical)
      // - 403 errors from thirdweb/cloudflare
      // - Fatal socket errors from relayer
      if (message.includes('Connection interrupted') || 
          message.includes('subscribe') ||
          message.includes('WebSocket') ||
          message.includes('thirdweb') ||
          message.includes('11155111.rpc.thirdweb.com') ||
          message.includes('403') ||
          message.includes('Reown Config') ||
          message.includes('WalletConnect') ||
          message.includes('Project not found') ||
          message.includes('Fatal socket error') ||
          message.includes('relayer') ||
          message.includes('socket') ||
          message.includes('Allowlist') ||
          message.includes('api.web3modal.org') ||
          message.includes('pulse.walletconnect.org') ||
          fullMessage.includes('thirdweb') ||
          fullMessage.includes('403') ||
          fullMessage.includes('relayer') ||
          fullMessage.includes('socket')) {
        return
      }
      originalError.apply(console, args)
    }

    return () => {
      window.console.error = originalError
      window.fetch = originalFetch
    }
  }, [])


  return (
    <div className="min-h-screen bg-black">
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#1a1a1a',
            color: '#fff',
            border: '1px solid #333',
            borderRadius: '12px',
          },
        }}
      />
      
      <Header />
      
      {/* Tab Navigation */}
      <div className="fixed top-16 left-0 right-0 z-40 bg-black/90 backdrop-blur-md border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex gap-1">
            <button
              onClick={() => setActiveTab('mint')}
              className={`px-4 py-3 text-sm font-medium transition-colors ${
                activeTab === 'mint'
                  ? 'text-[#d4a853] border-b-2 border-[#d4a853]'
                  : 'text-white/50 hover:text-white'
              }`}
            >
              Mint
            </button>
            <button
              onClick={() => setActiveTab('admin')}
              className={`px-4 py-3 text-sm font-medium transition-colors ${
                activeTab === 'admin'
                  ? 'text-[#d4a853] border-b-2 border-[#d4a853]'
                  : 'text-white/50 hover:text-white'
              }`}
            >
              Admin
            </button>
          </div>
        </div>
      </div>
      
      {activeTab === 'mint' ? (
        <>
          <HeroSection />
          
          <main className="max-w-6xl mx-auto px-4 py-16 space-y-12">
            <DutchAuctionTimer />
            <ContractStats />
            <MintSection />
            <div id="gallery">
              <MyNFTs />
            </div>
          </main>
        </>
      ) : (
        <main className="max-w-6xl mx-auto px-4 py-8 pt-32">
          <AdminDebug />
        </main>
      )}

      {/* Footer */}
      <footer className="border-t border-white/10 mt-20">
        <div className="max-w-6xl mx-auto px-4 py-8 text-center">
          <p className="text-white/30 text-sm">
            NFTWebToken • Smart Contract Audited • 7-Day Refund Guarantee
          </p>
          <p className="mt-3 text-white/40 text-sm">
            <a
              href="https://twitter.com/CosmicNFT1"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-white/70 transition-colors"
            >
              Twitter @CosmicNFT1
            </a>
          </p>
        </div>
      </footer>
    </div>
  )
}

function App() {
  const pathname = typeof window !== 'undefined' ? window.location.pathname : '/'
  const isLanding = pathname === '/' || pathname === '/index.html'

  return (
    <WagmiProvider config={config}>
      <QueryClientProvider client={queryClient}>
        <RainbowKitProvider
          theme={darkTheme({
            accentColor: '#d4a853',
            accentColorForeground: 'black',
            borderRadius: 'medium',
          })}
        >
          {isLanding ? <LandingPage /> : <DappShell />}
        </RainbowKitProvider>
      </QueryClientProvider>
    </WagmiProvider>
  )
}

export default App
